/**
 * 攒一笔 / 取出 —— 弹窗（I 域 · `DR-050` · 批 85）
 *
 * 依据：`AC-I2.1`–`I2.5`（2 腿 / 不计收支 / 备注留痕）· `AC-I2.4`（**取出用琥珀，非红**）·
 * `DR-050` D2（两账户同币种、不同一）· D3（未绑账户时首次绑定）
 *
 * ## 🔴 端侧纪律
 *
 * 1. **不发 `legs`** —— 腿结构在共享内核 `goalLegsOf()`（唯一实现）；本组件只说"动作 + 金额 + 账户"；
 * 2. **不做金额换算** —— 元文本 → 分 走 `yuanTextToCentsOrNull()`（唯一实现）；
 * 3. **取出用琥珀**（`theme.jiaying.tokens['c-notice']`）—— 红色在本项目 = 正/流入（铁律 8），
 *    用红色标"取出"会让颜色语义彻底反转。
 *
 * ## 为什么 Body 单独导出
 *
 * `Dialog` 走 Portal，`renderToStaticMarkup` 读不到内容 ⇒ 断言必须打在**内容件**上
 * （与 `ReimburseDialogBody` / `SettleForm` 同范式）。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  GOAL_MOVE_LABEL,
  GoalMove,
  formatMoneyText,
  type AccountView,
  type GoalMoveRequest,
  type GoalView,
} from '@jiaying/shared';
import {
  buildMoveRequest,
  emptyMoveDraft,
  moveDraftErrors,
  remainingText,
  type GoalMoveDraft,
} from '../features/goal.js';

export interface GoalMoveDialogBodyProps {
  goal: GoalView;
  /** 可选对手账户（**已按同币种过滤** —— 币种判定仍以服务端为准） */
  accounts: readonly AccountView[];
  today: string;
  busy: boolean;
  error: string | null;
  onChange: (next: GoalMoveDraft) => void;
  onSubmit: () => void;
  onClose: () => void;
  draft: GoalMoveDraft;
}

export function GoalMoveDialogBody({
  goal,
  accounts,
  busy,
  error,
  draft,
  onChange,
  onSubmit,
  onClose,
}: GoalMoveDialogBodyProps): ReactElement {
  const theme = useTheme();
  const errors = moveDraftErrors(draft, goal);
  const patch = (part: Partial<GoalMoveDraft>): void => onChange({ ...draft, ...part });
  const isWithdraw = draft.move === GoalMove.Withdraw;
  // 🔴 取出用琥珀（`AC-I2.4`）；攒钱用品牌色（中性）
  const accent = isWithdraw ? theme.jiaying.tokens['c-notice'] : theme.jiaying.tokens['c-brand'];

  return (
    <>
      <DialogTitle data-testid="goal-move-title" sx={{ color: accent }}>
        {GOAL_MOVE_LABEL[draft.move]} · {goal.name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            当前 {formatMoneyText(goal.savedCents, goal.currency)} / 目标{' '}
            {formatMoneyText(goal.targetCents, goal.currency)} —— {remainingText(goal)}
          </Typography>

          <TextField
            label={isWithdraw ? '取出金额（元）' : '攒入金额（元）'}
            value={draft.amountText}
            onChange={(e) => patch({ amountText: e.target.value })}
            error={errors.amount !== undefined}
            helperText={errors.amount ?? '这笔钱会在两个账户之间真实移动（不计入收支统计）'}
            inputProps={{ 'data-testid': 'goal-move-amount', inputMode: 'decimal' }}
            size="small"
            autoFocus
            fullWidth
          />

          <TextField
            label="日期"
            type="date"
            value={draft.date}
            onChange={(e) => patch({ date: e.target.value })}
            error={errors.date !== undefined}
            helperText={errors.date}
            inputProps={{ 'data-testid': 'goal-move-date' }}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />

          {goal.accountId === null && !isWithdraw && (
            <TextField
              select
              label="存到哪个账户"
              value={draft.hostAccountId}
              onChange={(e) => patch({ hostAccountId: e.target.value })}
              error={errors.hostAccountId !== undefined}
              helperText={
                errors.hostAccountId ?? '这个目标还没绑定账户；选一次就记住，之后不用再选'
              }
              SelectProps={{ native: true }}
              inputProps={{ 'data-testid': 'goal-move-host' }}
              size="small"
              fullWidth
            >
              <option value="">请选择</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </TextField>
          )}

          <TextField
            select
            label={isWithdraw ? '取出到' : '从哪个账户攒'}
            value={draft.counterAccountId}
            onChange={(e) => patch({ counterAccountId: e.target.value })}
            error={errors.counterAccountId !== undefined}
            helperText={errors.counterAccountId ?? '只能选同币种账户（跨币种请先做一笔转账）'}
            SelectProps={{ native: true }}
            inputProps={{ 'data-testid': 'goal-move-counter' }}
            size="small"
            fullWidth
          >
            <option value="">请选择</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </TextField>

          <TextField
            label="备注（可选）"
            value={draft.note}
            onChange={(e) => patch({ note: e.target.value })}
            inputProps={{ 'data-testid': 'goal-move-note', maxLength: 200 }}
            size="small"
            fullWidth
          />

          {error !== null && (
            <Alert severity="error" data-testid="goal-move-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} data-testid="goal-move-cancel">
          取消
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={busy} data-testid="goal-move-submit">
          {GOAL_MOVE_LABEL[draft.move]}
        </Button>
      </DialogActions>
    </>
  );
}

export interface GoalMoveDialogProps {
  /** `null` = 关闭 */
  goal: GoalView | null;
  accounts: readonly AccountView[];
  today: string;
  /** 默认动作（从卡片上的「攒一笔」/「取出」进入时不同） */
  initialMove: GoalMove;
  onClose: () => void;
  /** 提交；抛错时由外层把消息塞回 `error` */
  onSubmit: (req: GoalMoveRequest) => Promise<void>;
}

export function GoalMoveDialog({
  goal,
  accounts,
  today,
  initialMove,
  onClose,
  onSubmit,
}: GoalMoveDialogProps): ReactElement | null {
  const [draft, setDraft] = useState<GoalMoveDraft>(() => emptyMoveDraft(initialMove, today));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (goal === null) return null;

  const submit = (): void => {
    const errs = moveDraftErrors(draft, goal);
    if (Object.keys(errs).length > 0) {
      setError('请先把表单填完（金额 / 日期 / 账户）');
      return;
    }
    setBusy(true);
    setError(null);
    onSubmit(buildMoveRequest(draft, goal))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth data-testid="goal-move-dialog">
      <GoalMoveDialogBody
        goal={goal}
        accounts={accounts}
        today={today}
        busy={busy}
        error={error}
        draft={draft}
        onChange={setDraft}
        onSubmit={submit}
        onClose={onClose}
      />
    </Dialog>
  );
}
